import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Section, Cell, Input, Select, Spinner, Caption,
} from "@telegram-apps/telegram-ui";
import { api } from "../../lib/api.js";
import { usePageBack } from "../../lib/telegram.js";

const PAGE_SIZE = 100;

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU"); } catch { return iso; }
}

function actorLabel(e) {
  const name = [e.actor_first_name, e.actor_last_name].filter(Boolean).join(" ");
  if (name) return e.actor_username ? `${name} (@${e.actor_username})` : name;
  if (e.actor_username) return `@${e.actor_username}`;
  return e.actor_user_id ? `#${e.actor_user_id}` : "система";
}

export default function AuditLog() {
  usePageBack("/superadmin");
  const [params, setParams] = useSearchParams();
  const action = params.get("action") || "";
  const q = params.get("q") || "";
  const offset = Number(params.get("offset") || 0);

  const [draft, setDraft] = useState(q);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (action) qs.set("action", action);
    if (q) qs.set("q", q);
    api(`/api/v1/superadmin/audit?${qs}`).then(setData).catch((e) => setError(e.message));
  }, [action, q, offset]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams(params);
    if (draft) next.set("q", draft); else next.delete("q");
    next.delete("offset");
    setParams(next, { replace: true });
  };

  return (
    <>
      <section className="portal-card">
        <header className="portal-card__header">
          <h2 className="portal-card__title">Журнал аудита</h2>
          {data && <span className="portal-card__hint">Страница: {data.entries.length}</span>}
        </header>
        <form onSubmit={submitSearch} style={{ display: "grid", gap: 10, gridTemplateColumns: "2fr 1fr" }}>
          <div className="portal-search">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="7" cy="7" r="5" /><path d="m11 11 3 3" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder="Поиск по action / target_type"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={submitSearch}
            />
          </div>
          <select
            value={action}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("action", e.target.value);
              else next.delete("action");
              next.delete("offset");
              setParams(next, { replace: true });
            }}
            style={{
              font: "inherit", fontSize: 14, padding: "10px 14px",
              background: "var(--portal-surface)", color: "var(--portal-text)",
              border: "1px solid var(--portal-border)", borderRadius: 10, outline: "none",
            }}
          >
            <option value="">Любое действие</option>
            {data?.actions?.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </form>
      </section>

      <Section header={data ? `Записей на странице: ${data.entries.length}` : "Загрузка..."}>
        {error && <Cell multiline>Ошибка: {error}</Cell>}
        {!data && !error && <Cell><Spinner size="m" /></Cell>}
        {data?.entries?.map((e) => (
          <Cell
            key={e.id}
            multiline
            subtitle={`${formatDate(e.created_at)} · ${actorLabel(e)}${e.airfield_name ? ` · ${e.airfield_name}` : ""}`}
          >
            <strong>{e.action}</strong>
            {e.target_type && <> · {e.target_type}#{e.target_id}</>}
            <br />
            <Caption level="1">{summarisePayload(e.payload)}</Caption>
          </Cell>
        ))}
        {data && data.entries.length === 0 && (
          <Cell><Caption level="1">Записей нет.</Caption></Cell>
        )}
      </Section>

      <div className="portal-pager">
        <button
          type="button"
          className="portal-pager__btn"
          disabled={offset === 0}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("offset", String(Math.max(0, offset - PAGE_SIZE)));
            setParams(next, { replace: true });
          }}
        >← Назад</button>
        <span>записей: {data?.entries.length ?? 0}</span>
        <button
          type="button"
          className="portal-pager__btn"
          disabled={!data || data.entries.length < PAGE_SIZE}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.set("offset", String(offset + PAGE_SIZE));
            setParams(next, { replace: true });
          }}
        >Дальше →</button>
      </div>
    </>
  );
}

function summarisePayload(p) {
  if (!p || typeof p !== "object") return "";
  const keys = Object.keys(p);
  if (keys.length === 0) return "";
  // Compact JSON, truncated. Full payload is in the DB if needed.
  const s = JSON.stringify(p);
  return s.length > 200 ? s.slice(0, 200) + "…" : s;
}
